import { useMemo, useRef, useState } from "react";
import { clientToSvgUserSpace } from "../../utils/svgViewBoxPointer";
import { TrailPaths } from "./AgentTrailPaths";
import { FlowDebugSegmentLines } from "./FlowDebugSegmentLines";
import { useFlowAgents } from "./FlowAgentsContext";
import { useSvgViewBoxZoom } from "../../hooks/useSvgViewBoxZoom";

export function FlowAgentsViewer() {
    const {
        viewBoxAttr,
        width,
        height,
        debugSegments,
        showFlowField,
        activeAgents,
        completedPaths,
        completedUnionGroups,
        strokeColor,
        strokeWidth,
        smoothPaths,
        mergeTrailToSinglePath,
        filledVariableWidthStroke,
        trailTaperStartLength,
        trailTaperEndLength,
        unionGroupOnComplete,
        backgroundColor,
        canExport,
        handleDownload,
        spawnAgentAt,
        groupSize,
        groupRadius,
        inputMode,
        setPuppetBrushTarget,
        clearPuppetBrush,
    } = useFlowAgents();

    const base = useMemo(() => {
        const [x, y, w, h] = viewBoxAttr.split(" ").map(Number);
        return { x, y, w, h };
    }, [viewBoxAttr]);

    const {
        svgRef,
        viewBoxAttr: viewBox,
        panHandlers,
        zoomPercent,
        zoomIn,
        zoomOut,
        resetView,
    } = useSvgViewBoxZoom({ base });

    const [brushHover, setBrushHover] = useState<{
        x: number;
        y: number;
    } | null>(null);

    /** While pointer is down: start + current end for direction preview (clickSpawn) or paint session (dragPaint). */
    const dragRef = useRef<{
        x0: number;
        y0: number;
        x1: number;
        y1: number;
        pointerId: number;
        /** Set when inputMode is dragPaint after a successful spawn. */
        paintSpawnGroupId?: string;
    } | null>(null);
    const [dragPreview, setDragPreview] = useState<{
        x0: number;
        y0: number;
        x1: number;
        y1: number;
    } | null>(null);

    const SPAWN_DRAG_DIR_THRESHOLD = 4;

    const unionedGroupIds = useMemo(
        () => new Set(completedUnionGroups.map((g) => g.spawnGroupId)),
        [completedUnionGroups],
    );

    const handleSvgPointerDown = (event: React.PointerEvent<SVGSVGElement>) => {
        if (event.button !== 0) return;
        const pt = clientToSvgUserSpace(
            event.currentTarget,
            event.clientX,
            event.clientY,
            viewBox,
        );
        if (!pt) return;
        event.currentTarget.setPointerCapture(event.pointerId);

        if (inputMode === "dragPaint") {
            const gid = spawnAgentAt(pt.x, pt.y, null, {
                anchorLeaderAtPointer: true,
            });
            dragRef.current = {
                x0: pt.x,
                y0: pt.y,
                x1: pt.x,
                y1: pt.y,
                pointerId: event.pointerId,
                paintSpawnGroupId: gid,
            };
            if (gid) {
                setPuppetBrushTarget(gid, pt.x, pt.y);
            }
            setDragPreview(null);
            setBrushHover(null);
            return;
        }

        dragRef.current = {
            x0: pt.x,
            y0: pt.y,
            x1: pt.x,
            y1: pt.y,
            pointerId: event.pointerId,
        };
        setDragPreview({
            x0: pt.x,
            y0: pt.y,
            x1: pt.x,
            y1: pt.y,
        });
        setBrushHover(null);
    };

    const handleSvgPointerMove = (event: React.PointerEvent<SVGSVGElement>) => {
        const pt = clientToSvgUserSpace(
            event.currentTarget,
            event.clientX,
            event.clientY,
            viewBox,
        );
        if (!pt) return;
        const d = dragRef.current;
        if (d && d.pointerId === event.pointerId) {
            d.x1 = pt.x;
            d.y1 = pt.y;
            if (d.paintSpawnGroupId) {
                setPuppetBrushTarget(d.paintSpawnGroupId, pt.x, pt.y);
                if (groupSize > 1 && groupRadius > 0) {
                    setBrushHover({ x: pt.x, y: pt.y });
                }
            } else {
                setDragPreview({
                    x0: d.x0,
                    y0: d.y0,
                    x1: d.x1,
                    y1: d.y1,
                });
            }
        } else {
            setBrushHover(pt);
        }
    };

    const finishPointerInteraction = (
        target: SVGSVGElement,
        pointerId: number,
    ) => {
        const d = dragRef.current;
        if (!d || d.pointerId !== pointerId) return;
        dragRef.current = null;
        setDragPreview(null);
        try {
            target.releasePointerCapture(pointerId);
        } catch {
            /* already released */
        }
        if (d.paintSpawnGroupId) {
            clearPuppetBrush();
            return;
        }
        const dx = d.x1 - d.x0;
        const dy = d.y1 - d.y0;
        if (Math.hypot(dx, dy) < SPAWN_DRAG_DIR_THRESHOLD) {
            spawnAgentAt(d.x0, d.y0);
        } else {
            spawnAgentAt(d.x0, d.y0, { dx, dy });
        }
    };

    const handleSvgPointerUp = (event: React.PointerEvent<SVGSVGElement>) => {
        if (event.button !== 0) return;
        finishPointerInteraction(event.currentTarget, event.pointerId);
    };

    const handleSvgPointerCancel = (
        event: React.PointerEvent<SVGSVGElement>,
    ) => {
        const d = dragRef.current;
        if (!d || d.pointerId !== event.pointerId) return;
        dragRef.current = null;
        setDragPreview(null);
        if (d.paintSpawnGroupId) {
            clearPuppetBrush();
        }
        try {
            event.currentTarget.releasePointerCapture(event.pointerId);
        } catch {
            /* already released */
        }
    };

    const handleSvgPointerLeave = () => {
        if (dragRef.current) return;
        setBrushHover(null);
    };

    return (
        <div className="panel-viewer blob-field-viewer">
            <div className="path-tool-preview">
                <div className="svg-zoom-controls">
                    <button
                        type="button"
                        className="zoom-button"
                        onClick={zoomOut}
                        title="Zoom out"
                    >
                        −
                    </button>
                    <span className="zoom-level">{zoomPercent}%</span>
                    <button
                        type="button"
                        className="zoom-button"
                        onClick={zoomIn}
                        title="Zoom in"
                    >
                        +
                    </button>
                    <button
                        type="button"
                        className="zoom-button"
                        onClick={resetView}
                        title="Reset zoom"
                    >
                        ⌂
                    </button>
                </div>

                <div className="svg-scroll-wrapper">
                    <div className="svg-container svg-container--fill">
                        <svg
                            ref={svgRef}
                            viewBox={viewBox}
                            xmlns="http://www.w3.org/2000/svg"
                            className="path-tool-svg"
                            onPointerDown={(e) => {
                                panHandlers.onPointerDown(e);
                                handleSvgPointerDown(e);
                            }}
                            onPointerMove={(e) => {
                                panHandlers.onPointerMove(e);
                                handleSvgPointerMove(e);
                            }}
                            onPointerUp={(e) => {
                                panHandlers.onPointerUp(e);
                                handleSvgPointerUp(e);
                            }}
                            onPointerCancel={(e) => {
                                panHandlers.onPointerCancel(e);
                                handleSvgPointerCancel(e);
                            }}
                            onPointerLeave={handleSvgPointerLeave}
                            style={{ cursor: "crosshair", touchAction: "none" }}
                        >
                            <rect
                                width={width}
                                height={height}
                                fill={backgroundColor}
                            />
                            <g className="flow-agents-trails">
                                {completedPaths.map((p) =>
                                    unionGroupOnComplete &&
                                    unionedGroupIds.has(
                                        p.spawnGroupId,
                                    ) ? null : (
                                        <TrailPaths
                                            key={p.id}
                                            points={p.points}
                                            strokeWidths={p.strokeWidths}
                                            smoothPaths={smoothPaths}
                                            strokeColor={p.strokeColor}
                                            fallbackWidth={strokeWidth}
                                            mergeVaryingWidthToSinglePath={
                                                mergeTrailToSinglePath
                                            }
                                            filledVariableWidthStroke={
                                                filledVariableWidthStroke
                                            }
                                            strokeDashArray={undefined}
                                            trailTaperStartLength={
                                                trailTaperStartLength
                                            }
                                            trailTaperEndLength={
                                                trailTaperEndLength
                                            }
                                        />
                                    ),
                                )}
                                {unionGroupOnComplete &&
                                    completedUnionGroups.map((g) => (
                                        <path
                                            key={`${g.spawnGroupId}-union`}
                                            d={g.pathD}
                                            fill={g.fill}
                                            fillRule="nonzero"
                                            stroke="none"
                                        />
                                    ))}
                                {activeAgents.map((a) => (
                                    <TrailPaths
                                        key={a.id}
                                        points={a.points}
                                        strokeWidths={a.strokeWidths}
                                        smoothPaths={smoothPaths}
                                        strokeColor={a.strokeColor}
                                        fallbackWidth={strokeWidth}
                                        mergeVaryingWidthToSinglePath={
                                            mergeTrailToSinglePath
                                        }
                                        filledVariableWidthStroke={
                                            filledVariableWidthStroke
                                        }
                                        strokeDashArray={undefined}
                                        trailTaperStartLength={
                                            trailTaperStartLength
                                        }
                                        trailTaperEndLength={
                                            trailTaperEndLength
                                        }
                                    />
                                ))}
                            </g>
                            {showFlowField && debugSegments.length > 0 && (
                                <g
                                    className="flow-agents-field-debug"
                                    opacity={0.55}
                                    pointerEvents="none"
                                >
                                    <FlowDebugSegmentLines
                                        segments={debugSegments}
                                        strokeWidth={Math.max(
                                            0.4,
                                            Math.min(width, height) / 300,
                                        )}
                                        gradientIdPrefix="fa-flow-dbg"
                                    />
                                </g>
                            )}
                            {(brushHover || dragPreview) &&
                                groupSize > 1 &&
                                groupRadius > 0 && (
                                    <g
                                        className="flow-agents-brush-hover"
                                        aria-hidden
                                    >
                                        <circle
                                            className="svg-brush-hover-preview"
                                            cx={
                                                dragPreview
                                                    ? dragPreview.x0
                                                    : brushHover!.x
                                            }
                                            cy={
                                                dragPreview
                                                    ? dragPreview.y0
                                                    : brushHover!.y
                                            }
                                            r={groupRadius}
                                            stroke={strokeColor}
                                        />
                                    </g>
                                )}
                            {dragPreview && inputMode === "clickSpawn" && (
                                <g
                                    className="flow-agents-spawn-direction"
                                    aria-hidden
                                    pointerEvents="none"
                                >
                                    <line
                                        className="flow-agents-spawn-direction-line"
                                        x1={dragPreview.x0}
                                        y1={dragPreview.y0}
                                        x2={dragPreview.x1}
                                        y2={dragPreview.y1}
                                        stroke={strokeColor}
                                    />
                                </g>
                            )}
                            <g className="flow-agents-heads">
                                {activeAgents.map((a) => {
                                    const r = Math.max(
                                        2,
                                        a.liveStrokeWidth * 1.2,
                                    );
                                    const isGroupLeader =
                                        a.isGroupLeader === true;
                                    const isFollower = Boolean(a.leaderId);
                                    return (
                                        <circle
                                            key={`${a.id}-head`}
                                            cx={a.x}
                                            cy={a.y}
                                            r={r}
                                            fill={a.strokeColor}
                                            stroke={
                                                isGroupLeader
                                                    ? "#e8b832"
                                                    : isFollower
                                                      ? "rgba(255,255,255,0.4)"
                                                      : "none"
                                            }
                                            strokeWidth={
                                                isGroupLeader
                                                    ? 2
                                                    : isFollower
                                                      ? 1
                                                      : 0
                                            }
                                        />
                                    );
                                })}
                            </g>
                        </svg>
                    </div>
                </div>

                <div className="path-tool-footer">
                    <p className="path-tool-meta">
                        {completedPaths.length} finished - {activeAgents.length}{" "}
                        active - viewBox {viewBoxAttr}
                    </p>
                    <button
                        type="button"
                        className="generic-button"
                        disabled={!canExport}
                        onClick={handleDownload}
                    >
                        Download result SVG
                    </button>
                </div>
            </div>
        </div>
    );
}
